import { IconTrash } from "@tabler/icons-react";
import React from "react";
import { useTranslations } from "next-intl";
import {
  ButtonWithTooltip,
  ButtonWithTooltipProps,
} from "@components/ui/custom/button-with-tooltip";

/**
 * DeleteButton is a component designed to act as a button with an optional tooltip that displays
 * a localized "delete" message. It is styled for a minimalistic icon-button look by default, with
 * flexibility for rendering customizable child elements when provided.
 *
 * The button supports additional props for customization, including merging variants and styles
 * from external configurations through the `buttonVariants` prop.
 *
 * @param {Object} props - The properties for the DeleteButton component.
 * @param {React.ReactNode} [props.children] - Optional child elements or content to render inside the button.
 * If not provided, a default trash icon is displayed.
 * @param {boolean} [props.asChild] - Determines if the button should act as a wrapper for other components without adding styling.
 * @returns {JSX.Element} - A styled button with optional tooltip support for delete functionality.
 */
export const DeleteButton = ({ children, ...props }: ButtonWithTooltipProps) => {
  const tCommon = useTranslations("common");
  const onlyIcon = props.size?.includes("icon");

  return (
    <ButtonWithTooltip variant="ghost" tooltipContent={tCommon("words.verbs.delete")} {...props}>
      <IconTrash />
      {children && !onlyIcon && children}
    </ButtonWithTooltip>
  );
};
